import type { ReportTypeId } from '../config/reportTypes';
import type {
  DecisionAnalysisDomain,
  DecisionAnalysisInputEnvelopeV1,
} from './decisionAnalysis';
import type { TestFitAuthorityRef } from './generationImpact';
import type { PowerAvailabilityPreferences, ProjectIntent } from './siteRag';
import type { SiteStudyAreaScope } from './siteStudyArea';
import type { ScreeningArchitectureRecommendations } from '../lib/findings/screeningArchitectureRecommendations';

export const PATH_TO_GO_DECISION_SCHEMA_VERSION_V1 = 1 as const;
export const PATH_TO_GO_DECISION_SCHEMA_VERSION_V2 = 2 as const;
/**
 * Legacy writer alias. New writers select V2 when the evidence envelope
 * carries semantic analyses; keeping this alias at V1 preserves old imports.
 */
export const PATH_TO_GO_DECISION_SCHEMA_VERSION =
  PATH_TO_GO_DECISION_SCHEMA_VERSION_V1;

export type PathToGoDecisionStage = 1 | 2;
export type PathToGoFinalDecision =
  | 'GO'
  | 'CONDITIONAL'
  | 'NO_GO'
  | 'DEFER';

export type PathToGoScreeningDecision =
  | 'PROCEED'
  | 'PROCEED_WITH_CONCERNS'
  | 'DECLINE'
  | 'DEFER';
export type PathToGoHumanDecision = PathToGoFinalDecision | PathToGoScreeningDecision;

/** Missing stage on immutable legacy records means Stage 2. */
export function pathToGoDecisionStage(value: { decisionStage?: PathToGoDecisionStage }): PathToGoDecisionStage {
  if (value.decisionStage != null && value.decisionStage !== 1 && value.decisionStage !== 2) {
    throw new Error('Invalid decision stage.');
  }
  return value.decisionStage === 1 ? 1 : 2;
}

export interface PathToGoScreeningDecisionSnapshot {
  schemaVersion: 1;
  report: PathToGoDecisionReportSnapshot | null;
  reportContentHash: string | null;
  sourceHash: string | null;
  studyAreaScope: SiteStudyAreaScope | null;
  recommendations: ScreeningArchitectureRecommendations;
  reason: string | null;
}

export type PathToGoDecisionApprovalStatus =
  | 'submitted'
  | 'approved'
  | 'rejected';

export interface PathToGoSystemDecisionSnapshot {
  /** Live system result at the instant the human record was submitted. */
  band: 'go' | 'conditional' | 'conditionalHigh' | 'noGo' | 'withheld';
  label: string;
  composite: number | null;
  confidenceTag: string | null;
  dataAdequate: boolean;
  adequacyReason: string | null;
  coverageRatio: number;
}

export interface PathToGoDecisionReportSnapshot {
  id: string;
  reportType: ReportTypeId;
  version: number;
  minorVersion: number;
  createdAt: string;
  /** Present when this successful revision was used despite no longer being current. */
  savedBasis?: true;
  /**
   * Written only when the evidence was audit-flagged at capture. Absent —
   * never `false` — on every record captured before the flag existed, which
   * is what keeps those records byte-identical on rebuild.
   */
  auditRecommended?: true;
  sedId?: string;
  sedVersionUsed?: number;
  /** Present only for an authority-backed Test Fit revision. */
  testFitAuthorityRef?: TestFitAuthorityRef;
}

/** Immutable semantic analysis frozen into a V2 human decision record. */
export interface PathToGoDecisionAnalysisSnapshot {
  analysisId: string;
  domain: DecisionAnalysisDomain;
  inputHash: string;
  contentHash: string;
  snapshotPath: string;
  createdAt: string;
  /**
   * Exact SED, intent, typed-preference, strategy, upstream and Test Fit
   * authority identities. This is immutable evidence, not a live lookup.
   */
  input: DecisionAnalysisInputEnvelopeV1;
}

/**
 * Immutable evidence envelope carried by every human decision revision.
 * Rendering a historical decision never consults the current site/report/
 * analysis store, so later evidence changes cannot rewrite the record.
 *
 * `schemaVersion` and `analyses` are additive: both are absent on legacy V1
 * records. V2 writers set `schemaVersion: 2` and freeze at least one analysis.
 */
export interface PathToGoDecisionEvidenceSnapshot {
  decisionStage?: PathToGoDecisionStage;
  screeningPositiveDecisionGateSatisfied?: boolean;
  /** Explicit test-only decision scope; never a real-world investment approval.
   * Frozen from canonical site provenance, not selected by the client. */
  decisionScope?: { kind: 'hypothetical-scenario'; scenarioId: string; realWorldEligible: false };
  screening?: PathToGoScreeningDecisionSnapshot;
  schemaVersion?: (
    | typeof PATH_TO_GO_DECISION_SCHEMA_VERSION_V1
    | typeof PATH_TO_GO_DECISION_SCHEMA_VERSION_V2
  );
  capturedAt: string;
  intentHash: string;
  projectIntent: ProjectIntent | null;
  sedId: string | null;
  sedVersion: number | null;
  generationImpactApplicability: 'required' | 'grid-only' | 'undecided' | 'conflict';
  /** Exact accountable strategy value frozen outside Project Intent. */
  btmStrategy: NonNullable<PowerAvailabilityPreferences['btmStrategy']> | null;
  /** Distinguishes an explicit selection from a phase-inferred posture. */
  strategyDeclared: boolean;
  requiredReportTypes: ReportTypeId[];
  /** Frozen result of the complete Define → Build → Verify gate. */
  positiveDecisionGateSatisfied: boolean;
  system: PathToGoSystemDecisionSnapshot;
  reports: PathToGoDecisionReportSnapshot[];
  analyses?: PathToGoDecisionAnalysisSnapshot[];
  testFitAuthorityRef?: TestFitAuthorityRef;
}

export type PathToGoDecisionEvidenceSnapshotV1 =
  Omit<PathToGoDecisionEvidenceSnapshot, 'schemaVersion' | 'analyses'> & {
    schemaVersion?: typeof PATH_TO_GO_DECISION_SCHEMA_VERSION_V1;
    analyses?: never;
  };

export type PathToGoDecisionEvidenceSnapshotV2 =
  Omit<PathToGoDecisionEvidenceSnapshot, 'schemaVersion' | 'analyses'> & {
    schemaVersion: typeof PATH_TO_GO_DECISION_SCHEMA_VERSION_V2;
    analyses: PathToGoDecisionAnalysisSnapshot[];
  };

export interface PathToGoDecisionRecord {
  decisionStage?: PathToGoDecisionStage;
  /** Stage 1 retries share one create-only record; not present on legacy records. */
  submissionId?: string;
  submissionHash?: string;
  schemaVersion:
    | typeof PATH_TO_GO_DECISION_SCHEMA_VERSION_V1
    | typeof PATH_TO_GO_DECISION_SCHEMA_VERSION_V2;
  id: string;
  siteId: string;
  /** Monotonic site-local decision revision. */
  version: number;
  supersedesDecisionId?: string;
  decision: PathToGoHumanDecision;
  rationale: string;
  decisionOwner: string;
  authorUid: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
  approvalStatus: PathToGoDecisionApprovalStatus;
  submittedAt: string;
  approvedAt?: string;
  approvedByUid?: string;
  approvedByName?: string;
  rejectedAt?: string;
  rejectedByUid?: string;
  rejectedByName?: string;
  rejectionReason?: string;
  evidence: PathToGoDecisionEvidenceSnapshot;
}

export interface PathToGoDecisionRecordState {
  currentVersion: number;
  currentDecisionId: string;
  updatedAt: string;
}
